import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";
import BaseSwitch from "@reusables/BaseSwitch";
import BaseInput from "@reusables/BaseInput";
import BaseMaterialIconButton from "@reusables/BaseMaterialIconButton";
import ReservationLocationCommandDropdown, {
    listReservationLocations,
} from "@ready/ReservationLocationCommandDropdown";
import { cn } from "@helpers/utils";
import { useGetModuleProductQuery } from "@redux/features/products/productsApi";
import { useIsLocationReservationsEnabled } from "@redux/features/companies/useIsReservationsEnabled";
import { ReservationAllocationSchema } from "../../types";
import AllocationQuantityInput from "./AllocationQuantityInput";
import { getLocationKey, sumAllocations } from "./utils";

export interface ProductEntry {
    product: {
        id: number;
        name: string;
        code: string;
    };
    totalQuantity: number;
}

interface ReservationRowProps {
    entry: ProductEntry;
    allocations: ReservationAllocationSchema[]; // empty -> product is not reserved
    error?: string;
    // Separates this product from the one above it
    withDivider?: boolean;
    onToggle: (checked: boolean) => void;
    onAllocationsChange: (allocations: ReservationAllocationSchema[]) => void;
}

// Isolated row component to prevent re-renders of other rows
export default function ReservationRow({
    entry,
    allocations,
    error,
    withDivider = false,
    onToggle,
    onAllocationsChange
}: ReservationRowProps) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.reservations" });

    const isLocationReservationsEnabled = useIsLocationReservationsEnabled();

    const isReserved = allocations.length > 0;
    const reservedQuantity = sumAllocations(allocations);
    const remainingQuantity = entry.totalQuantity - reservedQuantity;
    const isOverReserved = reservedQuantity > entry.totalQuantity;

    // Per-location reservations keep their location controls even if the company has since disabled per-location reservations,
    // so the user sees what is going to be sent back instead of it being silently kept.
    const showLocations = isLocationReservationsEnabled || allocations.some((a) => a.location !== null);

    const { data: product } = useGetModuleProductQuery(
        { module: "sale", id: entry.product.id },
        { skip: !showLocations || !isReserved }
    );

    const productLocations = useMemo(() => product?.locations ?? [], [product]);
    const areLocationsLoading = !product;

    const usedLocationKeys = allocations.map((allocation) => getLocationKey(allocation.location));
    const usedLocationsSignature = usedLocationKeys.join("|");

    // Free locations in the order the picker lists them, so a new row gets the first one the user would see
    const freeLocations = useMemo(() => {
        const used = new Set(usedLocationsSignature.split("|"));

        return listReservationLocations(productLocations).filter(
            (location) => !used.has(getLocationKey(location))
        );
    }, [productLocations, usedLocationsSignature]);

    // Index of a just added row, whose location picker opens right away
    const [autoOpenIndex, setAutoOpenIndex] = useState<number | null>(null);

    const firstInputRef = useRef<HTMLInputElement>(null);
    const pendingFocusRef = useRef(false);

    // Focus input after enabling reservation via row click
    useEffect(() => {
        if (isReserved && pendingFocusRef.current) {
            pendingFocusRef.current = false;
            // Small delay to ensure input is enabled and rendered
            setTimeout(() => {
                firstInputRef.current?.focus();
                firstInputRef.current?.select();
            }, 50);
        }
    }, [isReserved]);

    const handleRowClick = (e: React.MouseEvent) => {
        // Don't trigger if clicking on interactive elements
        const target = e.target as HTMLElement;
        if (target.closest("input") || target.closest("button") || target.closest('[role="switch"]')) {
            return;
        }

        // Only handle click when not reserved
        if (!isReserved && entry.totalQuantity > 0) {
            pendingFocusRef.current = true;
            onToggle(true);
        }
    };

    const updateAllocation = (index: number, patch: Partial<ReservationAllocationSchema>) => {
        onAllocationsChange(allocations.map((allocation, i) => (i === index ? { ...allocation, ...patch } : allocation)));
    };

    const removeAllocation = (index: number) => {
        onAllocationsChange(allocations.filter((_, i) => i !== index));
    };

    const handleQuantityCommit = (index: number, quantity: number | null) => {
        // Each allocation is bounded by what the other allocations of the product have left
        const boundedQuantity =
            quantity === null ? 0 : Math.min(quantity, entry.totalQuantity - (reservedQuantity - allocations[index].quantity));

        if (boundedQuantity <= 0) {
            removeAllocation(index);
        } else {
            updateAllocation(index, { quantity: boundedQuantity });
        }
    };

    const handleLocationChange = (index: number, location: ReservationAllocationSchema["location"]) => {
        // The id is kept whatever the new location is, so the reservation is modified in place instead of being
        // cancelled and recreated. Turning a per-location reservation into a global one is sent as an explicit `locations: null`.
        updateAllocation(index, { location });
    };

    const canAddAllocation = !areLocationsLoading && freeLocations.length > 0 && remainingQuantity > 0;

    const addAllocationTip =
        !areLocationsLoading && freeLocations.length === 0
            ? t("allLocationsUsed")
            : remainingQuantity > 0
            ? t("buttons.addLocationRemaining", { count: remainingQuantity })
            : t("nothingLeftToReserve");

    const handleAddAllocation = () => {
        if (!canAddAllocation) {
            throw new Error(`An allocation can't be added for product #${entry.product.id} right now`);
        }

        setAutoOpenIndex(allocations.length);
        onAllocationsChange([...allocations, { location: freeLocations[0], quantity: remainingQuantity }]);
    };

    // Keeps the quantity column aligned between rows with and without location controls
    const trailingColumns = (suffix: React.ReactNode, action?: React.ReactNode) => (
        <>
            <div className="min-w-[40px] flex items-center flex-shrink-0">{suffix}</div>
            {showLocations && <div className="w-[32px] flex-shrink-0">{action}</div>}
        </>
    );

    return (
        <div
            className={cn(
                "flex items-start w-full py-2.5 px-4 text-sm transition-colors hover:bg-white/60 group",
                !isReserved && entry.totalQuantity > 0 && "cursor-pointer",
                withDivider && "border-0 border-t border-solid border-[#E5E5E5]"
            )}
            onClick={handleRowClick}
        >
            {/* Left: Toggle */}
            <div className="flex-shrink-0 flex items-center min-h-[40px]">
                <BaseSwitch
                    checked={isReserved}
                    onChange={(_, checked) => onToggle(checked)}
                    disabled={entry.totalQuantity === 0}
                />
            </div>

            {/* Center: Product info */}
            <div
                className={cn(
                    "ml-4 flex flex-col justify-center min-h-[40px]",
                    showLocations ? "flex-[2] min-w-[180px]" : "flex-1 min-w-0"
                )}
            >
                <div className="truncate">
                    <span className="text-accent">{entry.product.code}</span>
                    <span className="ml-3 text-accent">{entry.product.name}</span>
                </div>
                {error && <div className="text-xs text-[#E25C5C]">{error}</div>}
            </div>

            {/* Right: Allocations */}
            <div
                className={cn(
                    "flex flex-col gap-2 ml-4 pr-1",
                    // The location picker takes the free space, so long location names stay readable
                    showLocations ? "flex-[3] min-w-0" : "flex-shrink-0"
                )}
            >
                {!isReserved && (
                    <div className="flex items-center justify-end gap-2">
                        <div className="w-[100px] flex-shrink-0">
                            <BaseInput
                                value="—"
                                type="text"
                                disabled
                                sx={{
                                    "& input": {
                                        padding: "8px 12px",
                                        textAlign: "right",
                                        borderColor: "transparent",
                                        backgroundColor: "transparent",
                                        color: "#9ca3af",
                                        cursor: "pointer"
                                    }
                                }}
                            />
                        </div>
                        {trailingColumns(
                            <span className="text-sm text-gray-400 whitespace-nowrap">/ {entry.totalQuantity}</span>
                        )}
                    </div>
                )}

                {allocations.map((allocation, index) => {
                    const isLast = index === allocations.length - 1;

                    return (
                        <div key={index} className="flex items-center justify-end gap-2">
                            {showLocations && (
                                <div className="flex-1 min-w-[220px]">
                                    <ReservationLocationCommandDropdown
                                        size="sm"
                                        locations={productLocations}
                                        value={allocation.location}
                                        onChange={(location) => handleLocationChange(index, location)}
                                        excludedKeys={
                                            new Set(usedLocationKeys.filter((_, keyIndex) => keyIndex !== index))
                                        }
                                        isLoading={areLocationsLoading}
                                        autoOpen={autoOpenIndex === index}
                                        onAutoOpenHandled={() => setAutoOpenIndex(null)}
                                    />
                                </div>
                            )}

                            <div className="w-[100px] flex-shrink-0">
                                <AllocationQuantityInput
                                    ref={index === 0 ? firstInputRef : undefined}
                                    quantity={allocation.quantity}
                                    onCommit={(quantity) => handleQuantityCommit(index, quantity)}
                                />
                            </div>

                            {trailingColumns(
                                allocations.length === 1 ? (
                                    <span className="text-sm text-gray-400 whitespace-nowrap">/ {entry.totalQuantity}</span>
                                ) : (
                                    <BaseMaterialIconButton
                                        style="dim"
                                        size="small"
                                        icon={<X size={16} />}
                                        tip={t("buttons.removeLocation")}
                                        onClick={() => removeAllocation(index)}
                                    />
                                ),
                                isLast && (
                                    <BaseMaterialIconButton
                                        style="dim"
                                        size="small"
                                        icon={<Plus size={16} />}
                                        tip={addAllocationTip}
                                        disabled={!canAddAllocation}
                                        onClick={handleAddAllocation}
                                    />
                                )
                            )}
                        </div>
                    );
                })}

                {/* Total of a product split between locations, kept under the quantity column */}
                {allocations.length > 1 && (
                    <div className="flex items-center justify-end gap-2">
                        <span
                            className={cn(
                                "text-sm whitespace-nowrap pr-3",
                                isOverReserved ? "text-[#E25C5C]" : "text-gray-500"
                            )}
                        >
                            {t("total", { reserved: reservedQuantity, total: entry.totalQuantity })}
                        </span>
                        {trailingColumns(null)}
                    </div>
                )}
            </div>
        </div>
    );
}
